// ✅ src/pages/VehicleV2/VehicleLogBook.jsx
// FULL FRONTEND + SERVER-SIDE PAGINATION
// Backend: /api/vehicleLogBook

import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaArrowLeft,
  FaInfoCircle,
} from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/vehicleLogBook";

/* ---------------- helpers ---------------- */
const toStr = (v) => String(v ?? "").trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select" || s === "selec";
};

const openPopup = (url) => {
  const w = 900;
  const h = 900;
  const left = Math.max(0, Math.floor((window.screen.width - w) / 2));
  const top = Math.max(0, Math.floor((window.screen.height - h) / 2));

  window.open(
    url,
    "_blank",
    `width=${w},height=${h},top=${top},left=${left},resizable=yes,scrollbars=yes,toolbar=no,status=no,directories=no`
  );
};

export default function VehicleLogBook() {
  const navigate = useNavigate();
  const { theme, isDark, skin, setSkin } = useContext(ThemeContext);

  const resolvedSkin = useMemo(
    () => getCurrentSkin(theme || skin),
    [theme, skin]
  );

  const baseColor = resolvedSkin?.baseColor || "blue";
  const baseShade = resolvedSkin?.baseShade || "600";
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ---------------- localStorage session values ---------------- */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const sessionBranchId = localStorage.getItem("branchId") || "";
  const sessionBranchType = (
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO"
  ).toUpperCase();
  const userId = localStorage.getItem("userId") || "";

  const apiHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": sessionBranchId,
      "x-branchtype": sessionBranchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, sessionBranchId, sessionBranchType, userId]
  );

  /* ---------------- state ---------------- */
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);

  const [branches, setBranches] = useState([]);
  const [vehicleMainTypes, setVehicleMainTypes] = useState([]);

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [acRegNo, setAcRegNo] = useState([]);
  const [acVehicleName, setAcVehicleName] = useState([]);
  const [acVehicleCode, setAcVehicleCode] = useState([]);
  const acTimer = useRef(null);

  const [filters, setFilters] = useState({
    vehicleType: "O",
    regNo: "",
    branchId:
      sessionBranchType === "HO"
        ? "Select"
        : sessionBranchId || "Select",
    vehicleName: "",
    vehicleCode: "",
    vehicleMainType: "Select",
    status: "Select",
  });

  const initLoadedRef = useRef(false);

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFilters((p) => ({ ...p, [name]: value }));
  };

  const handleVehicleTypeChange = (v) => {
    setFilters((p) => ({
      ...p,
      vehicleType: v,
      regNo: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleMainType: "Select",
      status: "Select",
      branchId:
        sessionBranchType === "HO"
          ? "Select"
          : sessionBranchId || "Select",
    }));

    setAcRegNo([]);
    setAcVehicleName([]);
    setAcVehicleCode([]);
    setRows([]);
    setPage(1);
  };

  /* ---------------- dropdown loaders ---------------- */
  const loadBranches = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/branches`, {
        headers: apiHeaders,
      });

      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Failed to load branches");
      }

      setBranches(Array.isArray(res.data.rows) ? res.data.rows : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Branches load failed");
      setBranches([]);
    }
  }, [apiHeaders]);

  const loadVehicleMainTypes = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/vehicleMainTypes`, {
        headers: apiHeaders,
      });

      if (!res?.data?.success) {
        throw new Error(res?.data?.message || "Failed to load vehicle types");
      }

      setVehicleMainTypes(Array.isArray(res.data.rows) ? res.data.rows : []);
    } catch (err) {
      toast.error(
        err?.response?.data?.message || err.message || "Vehicle types load failed"
      );
      setVehicleMainTypes([]);
    }
  }, [apiHeaders]);

  /* ---------------- query builders ---------------- */
  const buildListParams = useCallback(
    (f, pageNo, size) => {
      const branchIdToSend =
        sessionBranchType === "HO"
          ? isSelect(f.branchId)
            ? "Select"
            : toStr(f.branchId)
          : sessionBranchId || "Select";

      return {
        page: String(pageNo),
        pageSize: String(size),
        vehicleType: toStr(f.vehicleType || "O"),
        regNo: toStr(f.regNo),
        vehicleName: toStr(f.vehicleName),
        vehicleCode: toStr(f.vehicleCode),
        branchId: branchIdToSend,
        vehicleMainType: isSelect(f.vehicleMainType)
          ? "Select"
          : toStr(f.vehicleMainType),
        status: isSelect(f.status) ? "Select" : toStr(f.status),
        includeHighlight: "1",
      };
    },
    [sessionBranchId, sessionBranchType]
  );

  const loadList = useCallback(
    async (f, pageNo = 1, size = pageSize, silent = false) => {
      setLoading(true);

      try {
        const res = await axios.get(`${API_BASE}/list`, {
          headers: apiHeaders,
          params: buildListParams(f, pageNo, size),
        });

        const data = res?.data || {};
        if (!data?.success) {
          throw new Error(data?.message || "Failed to load list");
        }

        setRows(Array.isArray(data.rows) ? data.rows : []);
        setTotalCount(Number(data.totalCount || 0));
        setTotalPages(Number(data.totalPages || 1));
        setPage(Number(data.page || pageNo));

        if (!silent) {
          toast.success(`Found ${Number(data.totalCount || 0)} records`);
        }
      } catch (err) {
        setRows([]);
        setTotalCount(0);
        setTotalPages(1);
        toast.error(err?.response?.data?.message || err.message || "List fetch failed");
      } finally {
        setLoading(false);
      }
    },
    [apiHeaders, buildListParams, pageSize]
  );

  /* ---------------- init ---------------- */
  const loadInit = useCallback(async () => {
    try {
      setPageLoading(true);
      await Promise.all([loadBranches(), loadVehicleMainTypes()]);
    } finally {
      setPageLoading(false);
    }
  }, [loadBranches, loadVehicleMainTypes]);

  useEffect(() => {
    loadInit();
  }, [loadInit]);

  useEffect(() => {
    if (pageLoading) return;
    if (initLoadedRef.current) return;
    initLoadedRef.current = true;
    loadList(filters, 1, pageSize, true);
  }, [pageLoading, filters, pageSize, loadList]);

  /* ---------------- autocomplete ---------------- */
  const getBranchForAC = useCallback(
    (f) => {
      if (sessionBranchType === "HO") {
        return isSelect(f.branchId) ? "Select" : toStr(f.branchId);
      }
      return sessionBranchId || "Select";
    },
    [sessionBranchId, sessionBranchType]
  );

  const runAC = useCallback(
    (kind, q) => {
      const query = toStr(q);

      if (!query) {
        if (kind === "regNo") setAcRegNo([]);
        if (kind === "vehicleName") setAcVehicleName([]);
        if (kind === "vehicleCode") setAcVehicleCode([]);
        return;
      }

      clearTimeout(acTimer.current);

      acTimer.current = setTimeout(async () => {
        try {
          const params = {
            q: query,
            vehicleType: filters.vehicleType,
            branchId: getBranchForAC(filters),
          };

          const res = await axios.get(`${API_BASE}/ac/${kind}`, {
            headers: apiHeaders,
            params,
          });

          if (!res?.data?.success) return;

          const items = (res.data.items || []).map((x) => toStr(x)).filter(Boolean);

          if (kind === "regNo") setAcRegNo(items);
          if (kind === "vehicleName") setAcVehicleName(items);
          if (kind === "vehicleCode") setAcVehicleCode(items);
        } catch {
          if (kind === "regNo") setAcRegNo([]);
          if (kind === "vehicleName") setAcVehicleName([]);
          if (kind === "vehicleCode") setAcVehicleCode([]);
        }
      }, 250);
    },
    [apiHeaders, filters, getBranchForAC]
  );

  /* ---------------- actions ---------------- */
  const handleSearch = async () => {
    setPage(1);
    await loadList(filters, 1, pageSize, false);
  };

  const handleViewAll = async () => {
    const reset = {
      vehicleType: filters.vehicleType,
      regNo: "",
      vehicleName: "",
      vehicleCode: "",
      vehicleMainType: "Select",
      branchId:
        sessionBranchType === "HO"
          ? "Select"
          : sessionBranchId || "Select",
      status: "Select",
    };

    setFilters(reset);
    setPage(1);
    setAcRegNo([]);
    setAcVehicleName([]);
    setAcVehicleCode([]);

    await loadList(reset, 1, pageSize, false);
  };

  /* ---------------- row actions ---------------- */
  const openMaster = (raw) => {
    const vehicleId = toStr(raw?.fld_vehicleid ?? raw?.vehicleId ?? raw?.VehicleId);
    const vehicleCode = toStr(raw?.fld_vehicle_code ?? raw?.vehicleCode);
    const detid = toStr(raw?.detid ?? raw?.detId ?? "");

    if (!vehicleId) {
      toast.error("vehicleId missing");
      return;
    }

    if (filters.vehicleType === "O") {
      const p = new URLSearchParams();
      p.set("vehicleId", vehicleId);
      openPopup(`/vehicle/OwnVehicle_DetailsEntry?${p.toString()}`);
    } else {
      const p = new URLSearchParams();
      p.set("logvehicleid", vehicleId);
      p.set("logvehiclecode", vehicleCode);
      p.set("logvehicledetid", detid);
      openPopup(`/vehicle/HireVehicle_DetailsEntry?${p.toString()}`);
    }
  };

  const openProgress = (raw) => {
    const vehicleId = toStr(raw?.fld_vehicleid ?? raw?.vehicleId);
    const vehicleCode = toStr(raw?.fld_vehicle_code ?? raw?.vehicleCode);
    const regno = toStr(raw?.fld_vehicle_regno ?? raw?.vehicleRegno);
    const name = toStr(raw?.fld_vehicle_name ?? raw?.vehicleName);

    const p = new URLSearchParams();
    p.set("prvehtype", filters.vehicleType === "O" ? "O" : "H");
    p.set("prvehicleid", vehicleId);
    p.set("prvehiclecode", vehicleCode);
    p.set("prvehicleregno", regno);
    p.set("prvehiclename", name);
    p.set(
      "prbranchid",
      isSelect(filters.branchId) ? "Select" : toStr(filters.branchId)
    );

    window.open(`/vehicle/ProgressMaster?${p.toString()}`, "_blank");
  };

  const openTransfer = (raw) => {
    const vehicleId = toStr(raw?.fld_vehicleid ?? raw?.vehicleId);
    const vehicleCode = toStr(raw?.fld_vehicle_code ?? raw?.vehicleCode);
    const regno = toStr(raw?.fld_vehicle_regno ?? raw?.vehicleRegno);

    const p = new URLSearchParams();
    p.set("trvehicleid", vehicleId);
    p.set("trvehiclecode", vehicleCode);
    p.set("trvehicleno", regno);
    p.set(
      "trbranchid",
      isSelect(filters.branchId) ? "Select" : toStr(filters.branchId)
    );

    openPopup(`/vehicle/vehicletransferreport?${p.toString()}`);
  };

  const openMaintenance = (raw) => {
    const vehicleId = toStr(
      raw?.fld_vehicleid ?? raw?.vehicleId ?? raw?.VehicleId ?? raw?.vid
    );
    const branchId = toStr(
      raw?.fld_branchid ??
        raw?.branchId ??
        raw?.BranchId ??
        (isSelect(filters.branchId) ? "Select" : filters.branchId)
    );
    const vehicleCode = toStr(
      raw?.fld_vehicle_code ?? raw?.vehicleCode ?? raw?.VehicleCode
    );
    const vehicleName = toStr(
      raw?.fld_vehicle_name ?? raw?.vehicleName ?? raw?.VehicleName
    );
    const regNo = toStr(
      raw?.fld_vehicle_regno ?? raw?.regNo ?? raw?.VehicleRegNo
    );

    const p = new URLSearchParams();
    p.set("mainvehicleid", vehicleId);
    p.set("mainbranchid", isSelect(branchId) ? "Select" : branchId);
    p.set("vehCode", vehicleCode);
    p.set("vehName", vehicleName);
    p.set("regNo", regNo);
    p.set("branchId", isSelect(branchId) ? "Select" : branchId);

    openPopup(`/vehicle/MaintenanceDetails?${p.toString()}`);
  };

  const openDiesel = (raw) => {
    const vehicleId = toStr(raw?.fld_vehicleid ?? raw?.vehicleId);
    const vehicleCode = toStr(raw?.fld_vehicle_code ?? raw?.vehicleCode);
    const detid = toStr(raw?.detid ?? raw?.detId ?? "");
    const regno = toStr(raw?.fld_vehicle_regno ?? raw?.vehicleRegno);

    const p = new URLSearchParams();
    p.set("logvehicleid", vehicleId);
    p.set("logvehiclecode", vehicleCode);
    p.set("logvehicledetid", detid);
    p.set(
      "logvehiclebranchid",
      isSelect(filters.branchId) ? "Select" : toStr(filters.branchId)
    );
    p.set("logvehicleregno", regno);

    openPopup(`/vehicle/dieselconsumption?${p.toString()}`);
  };

  const openSpare = (raw) => {
    const vehicleId = toStr(raw?.fld_vehicleid ?? raw?.vehicleId);
    const vehicleCode = toStr(raw?.fld_vehicle_code ?? raw?.vehicleCode);
    const detid = toStr(raw?.detid ?? raw?.detId ?? "");
    const regno = toStr(raw?.fld_vehicle_regno ?? raw?.vehicleRegno);

    const branchVal = isSelect(filters.branchId) ? "Select" : toStr(filters.branchId);

    const p = new URLSearchParams();
    p.set("Sparevehicleid", vehicleId);
    p.set("Sparevehiclecode", vehicleCode);
    p.set("Sparevehicledetid", detid);
    p.set("Sparevehiclebranchid", branchVal);
    p.set("Sparevehicleregno", regno);
    p.set("Sparebranchid", branchVal);

    window.open(`/vehicle/VehicleSparesDetailsReport?${p.toString()}`, "_blank");
  };

  /* ---------------- options ---------------- */
  const branchOptions = useMemo(() => {
    return (branches || [])
      .map((b) => ({
        value: toStr(
          b.fld_branchid ?? b.branchid ?? b.BranchId ?? b.id ?? b.value
        ),
        label: toStr(
          b.fld_BranchName ??
            b.fld_branchname ??
            b.branchname ??
            b.BranchName ??
            b.label ??
            b.value
        ),
      }))
      .filter((x) => x.value || x.label);
  }, [branches]);

  const vehicleTypeOptions = useMemo(() => {
    return (vehicleMainTypes || [])
      .map((v) => ({
        value: toStr(
          v.value ??
            v.fld_vehicletypeindex ??
            v.fld_vehiclemaintypeid ??
            v.id
        ),
        label: toStr(
          v.label ??
            v.fld_vehicletypename ??
            v.fld_vehiclemaintypename ??
            v.name ??
            v.value
        ),
      }))
      .filter((x) => x.label);
  }, [vehicleMainTypes]);

  const normRows = useMemo(() => {
    return (rows || []).map((r, idx) => ({
      _raw: r,
      sno: (page - 1) * pageSize + (idx + 1),
      vehicleMainType: toStr(r?.VehicleMainType ?? r?.vehicleMainType),
      vehicleCode: toStr(r?.fld_vehicle_code ?? r?.vehicleCode),
      vehicleName: toStr(r?.fld_vehicle_name ?? r?.vehicleName),
      vehicleRegno: toStr(r?.fld_vehicle_regno ?? r?.vehicleRegno),
      branchName: toStr(
        r?.fld_BranchName ?? r?.fld_branchname ?? r?.branchName ?? r?.Branch
      ),
      highlight: Boolean(r?._highlight ?? r?.highlight ?? r?.isHighlighted),
    }));
  }, [rows, page, pageSize]);

  /* ---------------- pagination ---------------- */
  const handlePrev = async () => {
    if (page <= 1) return;
    const p = page - 1;
    setPage(p);
    await loadList(filters, p, pageSize, true);
  };

  const handleNext = async () => {
    if (page >= totalPages) return;
    const p = page + 1;
    setPage(p);
    await loadList(filters, p, pageSize, true);
  };

  const handleGoTo = async (p) => {
    const safe = Math.min(Math.max(1, p), totalPages);
    setPage(safe);
    await loadList(filters, safe, pageSize, true);
  };

  const pageButtons = useMemo(() => {
    const btns = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, page + 2);

    if (start > 1) btns.push(1);
    for (let i = start; i <= end; i++) btns.push(i);
    if (end < totalPages) btns.push(totalPages);

    return [...new Set(btns)];
  }, [page, totalPages]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE LOG BOOK</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4 mb-5">
            <div className="flex items-center gap-4 flex-wrap">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Vehicle History
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalCount} Records
              </span>

              <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                INFRA SCREEN : B-49
              </span>

              {(loading || pageLoading) && (
                <span className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  Loading...
                </span>
              )}
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={handleSearch}
                disabled={loading || pageLoading}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-5 py-2 rounded-lg font-semibold shadow-md`}
              >
                <FaSearch className="w-4 h-4" />
                Search
              </button>

              <button
                onClick={handleViewAll}
                disabled={loading || pageLoading}
                className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded-lg font-semibold shadow-md"
              >
                <FaEye className="w-4 h-4" />
                View All
              </button>

              <button
                onClick={() => navigate(-1)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                }`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          <div className="flex items-center gap-6 mb-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="vehicleType"
                checked={filters.vehicleType === "O"}
                onChange={() => handleVehicleTypeChange("O")}
              />
              <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Own Vehicle
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                name="vehicleType"
                checked={filters.vehicleType === "H"}
                onChange={() => handleVehicleTypeChange("H")}
              />
              <span className={`${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Hire Vehicle
              </span>
            </label>
          </div>

          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <label className={labelClass}>Vehicle Reg.No</label>
                <input
                  list="acRegNo"
                  type="text"
                  name="regNo"
                  value={filters.regNo}
                  onChange={(e) => {
                    handleChange(e);
                    runAC("regNo", e.target.value);
                  }}
                  className={inputClass}
                  placeholder="Vehicle Reg.No"
                />
                <datalist id="acRegNo">
                  {acRegNo.map((x, i) => (
                    <option key={`${x}-${i}`} value={x} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className={labelClass}>Branch</label>
                <select
                  name="branchId"
                  value={filters.branchId}
                  onChange={(e) => {
                    handleChange(e);
                    setAcRegNo([]);
                    setAcVehicleName([]);
                    setAcVehicleCode([]);
                    setPage(1);
                  }}
                  className={inputClass}
                  disabled={sessionBranchType !== "HO"}
                >
                  <option value="Select">Select</option>
                  {branchOptions.map((b) => (
                    <option key={`${b.value}-${b.label}`} value={b.value}>
                      {b.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Vehicle Name</label>
                <input
                  list="acVehicleName"
                  type="text"
                  name="vehicleName"
                  value={filters.vehicleName}
                  onChange={(e) => {
                    handleChange(e);
                    runAC("vehicleName", e.target.value);
                  }}
                  className={inputClass}
                  placeholder="Vehicle Name"
                />
                <datalist id="acVehicleName">
                  {acVehicleName.map((x, i) => (
                    <option key={`${x}-${i}`} value={x} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className={labelClass}>Vehicle Code</label>
                <input
                  list="acVehicleCode"
                  type="text"
                  name="vehicleCode"
                  value={filters.vehicleCode}
                  onChange={(e) => {
                    handleChange(e);
                    runAC("vehicleCode", e.target.value);
                  }}
                  className={inputClass}
                  placeholder="Vehicle Code"
                />
                <datalist id="acVehicleCode">
                  {acVehicleCode.map((x, i) => (
                    <option key={`${x}-${i}`} value={x} />
                  ))}
                </datalist>
              </div>

              <div>
                <label className={labelClass}>Vehicle Main Type</label>
                <select
                  name="vehicleMainType"
                  value={filters.vehicleMainType}
                  onChange={(e) => {
                    handleChange(e);
                    setPage(1);
                  }}
                  className={inputClass}
                >
                  <option value="Select">Select</option>
                  {vehicleTypeOptions.map((v) => (
                    <option key={`${v.value}-${v.label}`} value={v.value}>
                      {v.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Vehicle Status</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={(e) => {
                    handleChange(e);
                    setPage(1);
                  }}
                  className={inputClass}
                >
                  <option value="Select">Select</option>
                  <option value="All">All</option>
                  <option value="Sale/Scrap">Sale/Scrap</option>
                </select>
              </div>

              <div>
                <label className={labelClass}>Page Size</label>
                <select
                  value={pageSize}
                  onChange={async (e) => {
                    const newSize = Number(e.target.value) || 10;
                    setPageSize(newSize);
                    setPage(1);
                    await loadList(filters, 1, newSize, true);
                  }}
                  className={inputClass}
                >
                  {[10, 25, 50, 100].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            {normRows.length === 0 ? (
              <div className="text-center py-16">
                <div
                  className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                    isDark ? "bg-gray-700" : "bg-gray-100"
                  }`}
                >
                  <FaInfoCircle className="w-10 h-10 text-gray-400" />
                </div>
                <h3
                  className={`text-xl font-semibold mb-2 ${
                    isDark ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {pageLoading ? "Loading..." : "No Records Found"}
                </h3>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className={`${buttonClass} text-white`}>
                    <tr>
                      <th className="px-4 py-3 text-center whitespace-nowrap">S.No</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Vehicle Main Type</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Vehicle Code</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Vehicle Name</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Vehicle Reg.No</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Branch</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Master Details</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Progress Details</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Transfer Details</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Maintenance History</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Diesel Consumption</th>
                      <th className="px-4 py-3 text-center whitespace-nowrap">Spare Life Detail</th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {normRows.map((r, idx) => (
                      <tr
                        key={`${r.vehicleCode || "row"}-${idx}`}
                        className={`${isDark ? "bg-gray-800" : "bg-white"} ${
                          isDark ? "hover:bg-gray-700" : "hover:bg-gray-50"
                        }`}
                        style={{ backgroundColor: r.highlight ? "tomato" : undefined }}
                      >
                        <td className="px-4 py-3 text-center">{r.sno}</td>
                        <td className="px-4 py-3 text-center">{r.vehicleMainType}</td>
                        <td className="px-4 py-3 text-center">{r.vehicleCode}</td>
                        <td className="px-4 py-3 text-center">{r.vehicleName}</td>
                        <td className="px-4 py-3 text-center">{r.vehicleRegno}</td>
                        <td className="px-4 py-3 text-center">{r.branchName}</td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => openMaster(r._raw)}
                            className={`underline font-semibold ${
                              isDark
                                ? "text-blue-100 hover:text-white"
                                : "text-blue-700 hover:text-blue-900"
                            }`}
                          >
                            View
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => openProgress(r._raw)}
                            className={`underline font-semibold ${
                              isDark
                                ? "text-blue-100 hover:text-white"
                                : "text-blue-700 hover:text-blue-900"
                            }`}
                          >
                            View
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => openTransfer(r._raw)}
                            className={`underline font-semibold ${
                              isDark
                                ? "text-blue-100 hover:text-white"
                                : "text-blue-700 hover:text-blue-900"
                            }`}
                          >
                            View
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => openMaintenance(r._raw)}
                            className={`underline font-semibold ${
                              isDark
                                ? "text-blue-100 hover:text-white"
                                : "text-blue-700 hover:text-blue-900"
                            }`}
                          >
                            View
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => openDiesel(r._raw)}
                            className={`underline font-semibold ${
                              isDark
                                ? "text-blue-100 hover:text-white"
                                : "text-blue-700 hover:text-blue-900"
                            }`}
                          >
                            View
                          </button>
                        </td>

                        <td className="px-4 py-3 text-center">
                          <button
                            type="button"
                            onClick={() => openSpare(r._raw)}
                            className={`underline font-semibold ${
                              isDark
                                ? "text-blue-100 hover:text-white"
                                : "text-blue-700 hover:text-blue-900"
                            }`}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {totalCount > 0 && (
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-5">
              <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Page <b>{page}</b> of <b>{totalPages}</b> • Showing <b>{rows.length}</b> rows •
                Total <b>{totalCount}</b>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={handlePrev}
                  disabled={page <= 1 || loading}
                  className={`px-4 py-2 rounded-lg border ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-800"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  } ${page <= 1 ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Prev
                </button>

                {pageButtons.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handleGoTo(p)}
                    disabled={loading}
                    className={`px-4 py-2 rounded-lg border font-semibold ${
                      p === page
                        ? `${buttonClass} text-white border-transparent`
                        : isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {p}
                  </button>
                ))}

                <button
                  type="button"
                  onClick={handleNext}
                  disabled={page >= totalPages || loading}
                  className={`px-4 py-2 rounded-lg border ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-800"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  } ${page >= totalPages ? "opacity-50 cursor-not-allowed" : ""}`}
                >
                  Next
                </button>
              </div>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}